import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils/axiosError';
import FilterAddCourse from '../components/Filter';
import { ApplicableCourse, SearchApplicableCoursePayload } from '../models/courseOrderLimit';
import { courseOrderLimitService } from '../services';
import '../style.scss';

// define props
interface DataTableProps {
  dataTable: ApplicableCourse[];
  setDataTable: (dataTable: ApplicableCourse[] | []) => void;
  onReflect: (
    selectedApplicableCourse: Array<{ id: number; name: string; menu_book_name: string; menu_book_id: number }>
  ) => void;
  exceptApplicableCourseIds?: number[];
  isShowModal: boolean;
  onClose: () => void;
}

function AddApplicableCourseModal({
  dataTable,
  setDataTable,
  onReflect,
  exceptApplicableCourseIds,
  isShowModal,
  onClose,
}: DataTableProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  //state
  const initialState = dataTable.reduce((o, key) => ({ ...o, [key.id]: false }), {});
  const [checked, setChecked] = useState<any>(initialState);
  const [dataTableFiltered, setDataTableFiltered] = useState<ApplicableCourse[]>(dataTable);
  const [allDataTable, setAllDataTable] = useState<ApplicableCourse[]>([]);
  const [menuBookName, setMenuBookName] = useState('');
  const [courseName, setCourseName] = useState('');

  useEffect(() => {
    getAllDataTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function transformedData(data: ApplicableCourse[]) {
    return data.map((item: any) => ({
      id: item.id,
      name: item.name,
      course_id: item.id,
      course_name: item.name,
      menu_book_id: item.menu_book_id,
      menu_book_name: item.menu_book_name,
      delete_flg: item.delete_flg,
    }));
  }
  // Table
  // Get all data from API
  async function getAllDataTable() {
    try {
      const payload: SearchApplicableCoursePayload = {
        courseName: '',
        menuBookName: '',
        limitType: 'all',
      };
      const response = await courseOrderLimitService.getCourseOrderLimitList(payload);
      const data = response.data;
      const dataTrans = transformedData(data);
      setAllDataTable(dataTrans);
    } catch (error) {
      if (!handleServiceError(showGlobalModal, error as AxiosError)) {
        displayError(showGlobalModal, error as AxiosError);
      }
    }
  }
  // Load all data (avoid available data)

  const loadAvailableCoursesData = async () => {
    const availableAddCourses = allDataTable.filter((availableCourse: ApplicableCourse) => {
      // Check if the item exists in `dataTable`
      const matchingItem = dataTable.find((dataItem) => dataItem.id === availableCourse.id);
      return !matchingItem || (matchingItem && matchingItem.is_select === false);
    });
    setDataTableFiltered(availableAddCourses);
  };

  useEffect(() => {
    loadAvailableCoursesData();
    // eslint-disable-next-line
  }, [allDataTable]);

  // handle checked all
  function handleCheckedAll() {
    const newState = dataTableFiltered.reduce((o, key) => ({ ...o, [key.id]: true }), {});
    setChecked(newState);
  }

  // handle unchecked all state changes
  function handleUncheckedAll() {
    const newState = dataTableFiltered.reduce((o, key) => ({ ...o, [key.id]: false }), {});
    setChecked(newState);
  }

  // toggle checkbox
  function toggleCheck(value: number) {
    let newState = { ...checked };
    newState[value] = !newState[value];
    setChecked(newState);
  }

  // Adding selected courses to the list applicable courses
  function handlOnReflect() {
    // Get the latest search results
    const latestSearchIds = dataTableFiltered.map((item) => item.id);
    const checkedIds = Object.keys(checked)
      .filter((key) => checked[key] === true)
      .map((value) => parseInt(value));

    // Intersect the checked IDs with the latest search IDs
    const idsToReflect = checkedIds.filter((id) => latestSearchIds.includes(id));
    const updatedDataTable = dataTable.map((item) => {
      if (idsToReflect.includes(item.id)) {
        return { ...item, is_select: true };
      }
      return item;
    });

    const updatedAllDataTable = allDataTable.map((item) => {
      if (idsToReflect.includes(item.id)) {
        return { ...item, is_select: true };
      }
      return item;
    });

    const newItems = allDataTable
      .filter((item) => idsToReflect.includes(item.id) && !dataTable.some((dtItem) => dtItem.id === item.id))
      .map((item) => ({
        ...item,
        is_select: true,
      }))
      .sort((a, b) => a.id - b.id);

    const selectedApplicableCourse: Array<{ id: number; name: string; menu_book_name: string; menu_book_id: number }> =
      newItems.map((dataItem) => {
        return {
          id: dataItem.id,
          name: dataItem.name,
          menu_book_name: dataItem.menu_book_name || '',
          menu_book_id: dataItem.menu_book_id || 0,
        };
      });

    // Show modal confirm
    showGlobalModal(() => (
      <ConfirmModal
        title={t('addApplicableCoursesModal.info')}
        message={t('addApplicableCoursesModal.add_warning')}
        onConfirm={() => {
          setDataTable([...updatedDataTable, ...newItems]);
          setAllDataTable([...updatedAllDataTable, ...newItems]);
          setChecked({});
          onClose();
          onReflect(selectedApplicableCourse);
        }}
      />
    ));
  }

  // Function to handle changes for both inputs
  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'menuBookName') {
      setMenuBookName(value);
    } else if (name === 'courseName') {
      setCourseName(value);
    }
  };

  // Function to handle filtering (example placeholder)
  async function callFilter(menuBookName: string, courseName: string) {
    const payload: SearchApplicableCoursePayload = {
      menuBookName: menuBookName,
      courseName: courseName,
      limitType: 'all', // Default value for limitType
    };

    try {
      const response = await courseOrderLimitService.getCourseOrderLimitList(payload);
      const data = response.data;
      const dataSearchTrans = transformedData(data);
      const filteredData = dataSearchTrans.filter((course) => {
        const matchingItem = dataTable.find((dataItem) => dataItem.id === course.id && !course.delete_flg);
        return !matchingItem || (matchingItem && matchingItem.is_select === false);
      });
      setDataTableFiltered(filteredData);
      setChecked({});
      return filteredData;
    } catch (error) {
      if (!handleServiceError(showGlobalModal, error as AxiosError)) {
        displayError(showGlobalModal, error as AxiosError);
      }
    }
  }

  return (
    <>
      <Modal dialogClassName="add-applicable-course-modal" show backdrop="static" onHide={onClose} centered>
        <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <FeatureTitleBar title={t('addApplicableCoursesModal.title')} />
        </Modal.Header>
        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          {
            <FilterAddCourse
              handleChangeName={handleChangeName}
              onCallFilter={() => callFilter(menuBookName, courseName)}
              menuBookName={menuBookName}
              courseName={courseName}
            />
          }
          <Row className="mt-4 mb-2">
            <Col sm={3} className="d-flex align-items-center">
              <button className="link" onClick={handleCheckedAll}>
                {t('addApplicableCoursesModal.select_all')}
              </button>
              <span className="d-inline-block mx-1">/</span>
              <button className="link" onClick={handleUncheckedAll}>
                {t('addApplicableCoursesModal.cancel_all')}
              </button>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button
                className="btn btn--dark-green"
                disabled={dataTableFiltered.every((item) => !checked[item.id])}
                onClick={handlOnReflect}
              >
                {t('applicableCoursesModal.add_button')}
              </Button>
            </Col>
          </Row>
          <div className="sticky-table" style={{ height: 360, maxHeight: 360 }}>
            <Table bordered style={{ borderRight: '1px solid #dee2e6' }}>
              <thead className="text-center" style={{ backgroundColor: '#36489a', color: '#fff' }}>
                <tr>
                  <th rowSpan={2} className="align-top" style={{ width: '10%' }}>
                    {t('applicableCoursesModal.delete_title')}
                  </th>
                  <th rowSpan={1} className="align-top" style={{ width: '30%' }}>
                    {t('applicableCoursesModal.menu_book_title')}
                  </th>
                  <th rowSpan={1} className="align-top" style={{ width: '60%' }}>
                    {t('applicableCoursesModal.course_title')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {dataTable &&
                  dataTableFiltered.map((value: ApplicableCourse) => (
                    <tr key={value.id}>
                      {exceptApplicableCourseIds?.includes(value.id) === false && !value.delete_flg && (
                        <>
                          {/* Checkbox column */}
                          <td style={{ width: '5%' }} className="text-center">
                            <Form.Check
                              type="checkbox"
                              value={value.id}
                              checked={checked[value.id] || false}
                              onChange={() => toggleCheck(value.id)}
                              disabled={exceptApplicableCourseIds?.includes(value.id)}
                            />
                          </td>
                          {/* Menu Book Name column */}
                          <td style={{ width: '25%' }} className="text-center">
                            <div className="" data-id={value.menu_book_id}>
                              {value.menu_book_name}
                            </div>
                          </td>
                          {/* Course Name column */}
                          <td style={{ width: '70%' }} className="text-center">
                            <div className="" data-id={value.course_id}>
                              {value.name}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default AddApplicableCourseModal;
